// BrandStudioBreadcrumb — Phase 1.6 (Architecture Review PRD v3)
//
// Tiny UI-only anchor that appears above every Brand Studio sub-route
// (NOT the home page itself). Each segment is a Link so the operator
// can jump up the journey one click at a time.
//
// Strict contract:
//   - NO backend calls
//   - NO route changes
//   - NO page rewrites
//   - Returns null on non-brand-studio paths (cheap no-op)
//
// Renders:
//   Brand Studio › Strengthen › Brand Diagnostic
//   Brand Studio › Protect › Trademark Readiness
//   Brand Studio › Health & Performance › Brand Performance
//
// Mounted in Layout.jsx so it appears once above every mirrored page
// without touching the page components themselves.
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

// Static URL → (section_label, section_path, page_label) map.
// Kept inline (no backend trips). When a new sub-route is added, the
// map gets one line — no other code changes.
const MAP = {
  "foundation/setup":             { section: "Foundation",          page: "Brand Setup",              accent: "#6366F1" },
  "foundation/identity":          { section: "Foundation",          page: "Brand Identity",           accent: "#6366F1" },
  "foundation/repository":        { section: "Foundation",          page: "Brand Repository",         accent: "#6366F1" },
  "foundation/voice":             { section: "Foundation",          page: "Brand Voice",              accent: "#6366F1" },
  "foundation/launch":            { section: "Foundation",          page: "Brand Launch",             accent: "#6366F1" },
  "protect/trademark-readiness":  { section: "Protect",             page: "Trademark Readiness",      accent: "#F59E0B" },
  "protect/domain-portfolio":     { section: "Protect",             page: "Domain Portfolio",         accent: "#F59E0B" },
  "protect/identity-analysis":    { section: "Protect",             page: "Brand Identity Analysis",        accent: "#F59E0B" },
  "protect/brand-protection-workspace": { section: "Protect",       page: "Brand Protection Workspace", accent: "#F59E0B" },
  "protect/evidence-center":      { section: "Protect",             page: "Evidence Center",          accent: "#F59E0B" },
  "strengthen/diagnostic":        { section: "Strengthen",          page: "Brand Diagnostic",         accent: "#8B5CF6" },
  "strengthen/positioning":       { section: "Strengthen",          page: "Positioning",              accent: "#8B5CF6" },
  "strengthen/audience":          { section: "Strengthen",          page: "Audience Intelligence",    accent: "#8B5CF6" },
  "strengthen/competitor":        { section: "Strengthen",          page: "Competitor Intelligence",  accent: "#8B5CF6" },
  "brand-health":                 { section: "Health & Performance", page: "Brand Health",            accent: "#22D3EE", flat: true },
  "brand-performance":            { section: "Health & Performance", page: "Brand Performance",       accent: "#A855F7", flat: true },
  "steward/living-bi":            { section: "Steward",             page: "Living Brand Intelligence",accent: "#10B981" },
};

// Section → opens-this-section navigation. The sidebar already
// auto-expands when a Brand Studio sub-route loads, so we land on
// the section's *first* page (one click rather than scrolling).
const SECTION_LANDING = {
  "Foundation":           "/brand-studio/foundation/setup",
  "Protect":              "/brand-studio/protect/trademark-readiness",
  "Strengthen":           "/brand-studio/strengthen/diagnostic",
  "Health & Performance": "/brand-studio/brand-health",
  "Steward":              "/brand-studio/steward/living-bi",
};

export default function BrandStudioBreadcrumb() {
  const location = useLocation();
  if (!location.pathname.startsWith("/brand-studio/")) return null;

  // Sprint 2026-06-11 PM-8 Phase 1 (paco) — on canonical Brand pages the
  // new 2-layer breadcrumb (Mission row + active-pillar children row)
  // already conveys position. The legacy text crumb becomes redundant —
  // suppress it on Phase-1 paths.
  // Sprint 2026-06-11 PM-9 Phase 2 (paco) — every Brand leaf route now
  // renders the canonical 2-row breadcrumb (no legacy text crumb needed).
  const PHASE1_CANONICAL_PATHS = [
    "/brand-studio/foundation/setup",
    "/brand-studio/foundation/identity",
    "/brand-studio/foundation/voice",
    "/brand-studio/foundation/repository",
    "/brand-studio/foundation/launch",
    "/brand-studio/protect/identity-analysis",
    "/brand-studio/protect/domain-portfolio",
    "/brand-studio/protect/trademark-readiness",
    "/brand-studio/protect/evidence-center",
    "/brand-studio/strengthen/diagnostic",
    "/brand-studio/strengthen/positioning",
    "/brand-studio/strengthen/audience",
    "/brand-studio/strengthen/competitor",
    "/brand-studio/brand-health",
    "/brand-studio/brand-performance",
    "/brand-studio/steward/living-bi",
  ];
  if (PHASE1_CANONICAL_PATHS.includes(location.pathname)) return null;

  // Strip leading "/brand-studio/" to look up the static map.
  const key = location.pathname.replace(/^\/brand-studio\//, "");
  const entry = MAP[key];
  if (!entry) return null;

  const sectionPath = SECTION_LANDING[entry.section] || "/brand-studio";

  return (
    <nav
      aria-label="Brand Studio breadcrumb"
      className="mb-4 -mt-1 text-[11px] leading-tight"
      data-testid="brand-studio-breadcrumb"
    >
      <ol className="flex items-center gap-1 flex-wrap text-[var(--app-text-muted)]">
        <li>
          <Link
            to="/brand-studio"
            className="font-semibold hover:text-[var(--app-text)] transition-colors"
            data-testid="bs-bc-home"
          >
            Brand Studio
          </Link>
        </li>
        <li aria-hidden>
          <ChevronRight className="w-3 h-3 opacity-50" />
        </li>
        <li>
          <Link
            to={sectionPath}
            className="font-semibold hover:text-[var(--app-text)] transition-colors"
            style={{ color: entry.accent }}
            data-testid="bs-bc-section"
          >
            {entry.section}
          </Link>
        </li>
        <li aria-hidden>
          <ChevronRight className="w-3 h-3 opacity-50" />
        </li>
        <li
          className="font-bold text-[var(--app-text)] truncate max-w-[42ch]"
          aria-current="page"
          data-testid="bs-bc-leaf"
        >
          {entry.page}
        </li>
      </ol>
    </nav>
  );
}
